import React from 'react'
import {
  ButtonBox,
  StyledDialog,
  DialogBox,
  DialogTitle,
  StyledCloseIcon,
  StudyModeBox,
  StudyModeItem,
  IconBox,
  StudyModeTitle,
  StudyModeText,
  BackButton,
  DialogText,
  ExamStatusBox,
  UpgradeBox,
} from './styles'
import {Button} from '../../../../components/button'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import FlexibleStudyIcon from '../../../../images/icons/new-dashboard/open-book-icon.svg'
import GuidedStudyProcessIcon from '../../../../images/icons/new-dashboard/proven-process-icon.svg'
import GuidedStudyProcessIconDisabled from '../../../../images/icons/new-dashboard/proven_process_logo_disabled.svg'
import {StudyPlanStep, StudyPlanStudyMode} from '.'
import {Link} from 'react-router-dom'
import DivisionDatepicker from '../../DivisionDatepicker'
import {StudyGuide} from '../../../../redux/studyGuides'
import {Attempt} from '../../../../redux/reportExamStatus'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../../../redux/members'

type Props = {
  open: boolean
  onClose: () => void
  onChangeStep: (value: StudyPlanStep) => void
  step: StudyPlanStep
  onChangeStudyMode: (value: StudyPlanStudyMode) => void
  studyMode: StudyPlanStudyMode
  onChangeDateFilter: (value: string) => void
  dateFilter: string
  onSubmitExamDate: () => void
  onChangeDays: (value: number) => void
  days: number
  studyGuides?: StudyGuide[]
  onSubmit: (studyGuide?: StudyGuide, migrate?: boolean) => void
  isSubmitting: boolean
  reportStatus?: Attempt
  studyGuide?: StudyGuide
  onTrackUpgrade: () => void
}

const View = ({
  open,
  onClose,
  onChangeStep,
  step,
  onChangeStudyMode,
  studyMode,
  onChangeDateFilter,
  dateFilter,
  onSubmitExamDate,
  onChangeDays,
  days,
  studyGuides,
  onSubmit,
  isSubmitting,
  reportStatus,
  studyGuide,
  onTrackUpgrade,
}: Props) => {
  const access = useSelector(selectMemberAccess)

  const isGuided = studyGuide ? studyGuide?.timeWindow > 0 : false

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.study_guides?.value) === 1
      : false

  const flexibleSg = studyGuides?.find(sg => sg.timeWindow === 0)
  const guidedSg = studyGuides
    ?.filter(sg => sg.timeWindow > 0)
    .sort((a, b) => a.timeWindow - b.timeWindow)

  const recommendedStudyPlan = () => {
    if ((reportStatus || dateFilter) && guidedSg) {
      const today = new Date().getTime()
      const examDate = new Date(reportStatus?.examDate ?? dateFilter).getTime()

      const diffDays = Math.ceil((examDate - today) / (1000 * 60 * 60 * 24))

      const timeWindows = guidedSg.sort((a, b) => a.timeWindow - b.timeWindow)

      if (diffDays < timeWindows[0].timeWindow) {
        return timeWindows[0].timeWindow
      }

      for (let i = 0; i < timeWindows.length - 1; i++) {
        if (
          diffDays >= timeWindows[i].timeWindow &&
          diffDays < timeWindows[i + 1].timeWindow
        ) {
          return timeWindows[i].timeWindow
        }
      }

      return timeWindows[timeWindows.length - 1].timeWindow
    }
  }

  const renderDialogBox = () => {
    if (step === 'study-mode') {
      return (
        <>
          <StudyModeBox>
            <StudyModeItem
              $isActive={studyMode === 'flexible'}
              onClick={() => onChangeStudyMode('flexible')}
            >
              <IconBox>
                <img src={FlexibleStudyIcon} alt="Flexible Study" />
              </IconBox>
              <StudyModeTitle>Flexible Study</StudyModeTitle>
              <StudyModeText>
                Access all study materials and set your own pace.
              </StudyModeText>
            </StudyModeItem>
            {guidedSg && guidedSg.length > 0 && hasAccess && (
              <StudyModeItem
                $isActive={studyMode === 'guided'}
                onClick={() => onChangeStudyMode('guided')}
              >
                <IconBox>
                  <img
                    src={GuidedStudyProcessIcon}
                    alt="Guided study process"
                  />
                </IconBox>
                <StudyModeTitle>Guided Process</StudyModeTitle>
                <StudyModeText>
                  Follow a personalized study plan aligned with your exam
                  schedule to succeed.
                </StudyModeText>
              </StudyModeItem>
            )}
            {!hasAccess && (
              <StudyModeItem
                $isActive={studyMode === 'guided'}
                $isDisabled={true}
                onClick={onTrackUpgrade}
              >
                <IconBox>
                  <img
                    src={GuidedStudyProcessIconDisabled}
                    alt="Guided study process"
                  />
                </IconBox>
                <StudyModeTitle $isDisabled={true}>
                  Guided Process
                </StudyModeTitle>
                <StudyModeText $isDisabled={true}>
                  Follow a personalized study plan aligned with your exam
                  schedule to succeed.
                </StudyModeText>

                <UpgradeBox>Upgrade to Expert</UpgradeBox>
              </StudyModeItem>
            )}
          </StudyModeBox>
          <ButtonBox>
            <Button
              children={'Continue'}
              color={'secondary'}
              size={'medium'}
              disabled={!studyMode || isSubmitting}
              onClick={() =>
                studyMode === 'flexible'
                  ? onSubmit(flexibleSg)
                  : onChangeStep(reportStatus ? 'days' : 'exam-status')
              }
            />
          </ButtonBox>
        </>
      )
    }

    if (step === 'exam-status') {
      return (
        <>
          <ExamStatusBox>
            <DialogText size="large">
              Enter your exam date to customize your study path.
            </DialogText>
            <DivisionDatepicker
              division={null}
              onChangeDateFilter={onChangeDateFilter}
              dateFilter={dateFilter}
            />
            <DialogText size="small">
              If you haven’t scheduled your exam yet, <br />
              <Link to={'/profile/settings/are-progress'}>click here</Link> to
              get started.
            </DialogText>
          </ExamStatusBox>
          <ButtonBox>
            <Button
              children={'Submit'}
              color={'secondary'}
              size={'medium'}
              disabled={!dateFilter || isSubmitting}
              onClick={onSubmitExamDate}
            />
            <Button
              children={'Skip'}
              color={'white'}
              size={'medium'}
              onClick={() => onChangeStep('days')}
            />
          </ButtonBox>
          <BackButton onClick={() => onChangeStep('study-mode')}>
            <KeyboardBackspaceIcon /> Back
          </BackButton>
        </>
      )
    }

    if (step === 'days') {
      return (
        <>
          <StudyModeBox>
            {guidedSg?.map((sg, key) => {
              if (studyGuide?.id === sg.id) {
                return null
              }

              return (
                <React.Fragment key={`sp-item-${key}`}>
                  <StudyModeItem
                    $isCentered={true}
                    $isActive={days === sg.timeWindow}
                    $isRecommended={recommendedStudyPlan() === sg.timeWindow}
                    onClick={() => onChangeDays(sg.timeWindow)}
                  >
                    <StudyModeTitle>
                      {sg.timeWindow} Day
                      <br />
                      Study Plan
                    </StudyModeTitle>
                    {recommendedStudyPlan() === sg.timeWindow && (
                      <StudyModeText>
                        Recommended for you based on your exam date.
                      </StudyModeText>
                    )}
                  </StudyModeItem>
                </React.Fragment>
              )
            })}
          </StudyModeBox>
          <ButtonBox>
            <Button
              children={'Continue'}
              color={'secondary'}
              size={'medium'}
              disabled={!days || isSubmitting}
              onClick={() => {
                const studyGuide = guidedSg?.find(sg => sg.timeWindow === days)
                if (studyGuide?.hasProgress && !isGuided) {
                  onChangeStep('migrate')
                } else {
                  onSubmit(studyGuide, false)
                }
              }}
            />
          </ButtonBox>
          <BackButton
            onClick={() =>
              onChangeStep(reportStatus ? 'study-mode' : 'exam-status')
            }
          >
            <KeyboardBackspaceIcon /> Back
          </BackButton>
        </>
      )
    }

    if (step === 'migrate') {
      return (
        <>
          <ExamStatusBox>
            <DialogText size="large">
              We noticed you have existing study progress on the self guided
              dashboard. You can choose to migrate your progress into your new
              study guide or start fresh.
            </DialogText>
          </ExamStatusBox>
          <ButtonBox style={{flexFlow: 'row'}}>
            <Button
              children={'Migrate My Progress'}
              color={'secondary'}
              size={'medium'}
              disabled={isSubmitting}
              onClick={() =>
                onSubmit(guidedSg?.find(sg => sg.timeWindow === days), true)
              }
              style={{width: '100%'}}
            />
            <Button
              children={'Start Fresh'}
              color={'primary'}
              size={'medium'}
              disabled={isSubmitting}
              onClick={() =>
                onSubmit(guidedSg?.find(sg => sg.timeWindow === days), false)
              }
              style={{width: '100%'}}
            />
          </ButtonBox>
          <BackButton onClick={() => onChangeStep('days')}>
            <KeyboardBackspaceIcon /> Back
          </BackButton>
        </>
      )
    }
  }

  return (
    <StyledDialog maxWidth={'md'} onClose={onClose} open={open}>
      <DialogBox>
        <DialogTitle>
          {step === 'study-mode' ? (
            <>Choose Your Study Mode</>
          ) : step === 'exam-status' ? (
            <>Exam Status</>
          ) : step === 'days' ? (
            <>
              Choose Your Study Plan
              <br />
              to start your journey
            </>
          ) : (
            <>
              Would you like to migrate
              <br />
              your study progress?
            </>
          )}
          <StyledCloseIcon onClick={onClose} className="x-button" />
        </DialogTitle>
        {renderDialogBox()}
      </DialogBox>
    </StyledDialog>
  )
}

export default View
